import React, { useEffect, useRef } from 'react';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';

import { Spinner } from 'react-bootstrap';

import { useSelector, useDispatch } from 'react-redux';
import { updateSampleAndReport } from '../../store/features/sampleSlice';


const soilUpdateSchema = Yup.object({
  sample_id: Yup.string().required('Sample ID is required'),
  ph: Yup.string().required('PH Value is required'),
  ec: Yup.string().required('EC Value is required'),
  oc: Yup.string().required('OC Value is required'),
  nitrate: Yup.string().required('Nitrate Value is required'),
  ammonia: Yup.string().required('Ammonia Value is required'),
  phosphorus: Yup.string().required('Phosphorus Value is required'),
  potassium: Yup.string().required('Potassium Value is required'),
  farm_name: Yup.string().required('Farm Name is required'),
  farm_size: Yup.string().required('Farm Size is required'),
  previous_crop: Yup.string().required('Previous Crop is required'),
  next_crop: Yup.string().required('Next Crop is required'),
});

const waterUpdateSchema = Yup.object({
  sample_id: Yup.string().required('Sample ID is required'),
  farm_name: Yup.string().required('Farm name is required'),
  carbonate: Yup.string().required('Carbonate Value is required'),
  bicarbonate: Yup.string().required('Bicarbonate Value is required'),
  tds: Yup.string().required('TDS Value is required'),
});

const generateClassName = (errors, touched, fieldName) => {
  const hasError = errors[fieldName] && touched[fieldName];
  const isSuccess = touched[fieldName];
  return `form-control ${
    hasError ? 'border-2 border-danger' : isSuccess && 'border-2 border-success'
  }`;
};

const NumberField = ({ field, form, ...props }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    const input = inputRef.current;

    const handleWheel = (event) => {
      if (document.activeElement === input) {
        event.preventDefault();
      }
    };

    input.addEventListener('wheel', handleWheel);

    return () => {
      input.removeEventListener('wheel', handleWheel);
    };
  }, []);

  return <input ref={inputRef} type='number' {...field} {...props} />;
};

function UpdateForm({ sampleDetails }) {
  const dispatch = useDispatch();
  const { updateStatus } = useSelector(
    (state) => state.sample
  );

  const isWaterReport = sampleDetails.sample_type === 'WATER';

  const currentSchema = isWaterReport ? waterUpdateSchema : soilUpdateSchema;

  const schemaDescription = currentSchema.describe();

  const isRequired = (field) =>
    schemaDescription.fields[field]?.tests?.some(
      (test) => test.name === 'required'
    );

  const renderLabel = (field, label) => (
    <Form.Label htmlFor={field}>
      {label}
      {isRequired(field) && (
        <span className="text-danger">&nbsp;*</span>
      )}
    </Form.Label>
  );

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        value_type: 'RAW',
        sample_type: sampleDetails.sample_type,
        sample_id: sampleDetails.sample_id,
        farm_name: sampleDetails.farm_name,
        farm_size: sampleDetails.farm_size ?? "",
        previous_crop: sampleDetails.previous_crop ?? "",
        next_crop: sampleDetails.next_crop ?? "",
        ph: sampleDetails.ph ?? "",
        ec: sampleDetails.ec ?? "",
        oc: sampleDetails.oc ?? "",
        nitrate: sampleDetails.nitrate ?? "",
        ammonia: sampleDetails.ammonia ?? "",
        phosphorus: sampleDetails.phosphorus ?? "",
        potassium: sampleDetails.potassium ?? "",
        calcium: sampleDetails.calcium ?? "",
        magnesium: sampleDetails.magnesium ?? "",
        sulphur: sampleDetails.sulphur ?? "",
        boron: sampleDetails.boron ?? "",
        iron: sampleDetails.iron ?? "",
        zinc: sampleDetails.zinc ?? "",
        copper: sampleDetails.copper ?? "",
        carbonate: sampleDetails.carbonate ?? "",
        bicarbonate: sampleDetails.bicarbonate ?? "",
        tds: sampleDetails.tds ?? "",
      }}
      validationSchema={isWaterReport ? waterUpdateSchema : soilUpdateSchema}
      onSubmit={(values, { resetForm }) => {
        dispatch(updateSampleAndReport({ values }))
          .then(() => {
            resetForm();
          })
          .catch((err) => {
            console.log('Error while creating report: ', err);
          });
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        setFieldValue,
        setFieldTouched,
      }) => (
        <div className='p-4'>
          <Form
            className='form__details'
            onSubmit={handleSubmit}
            onChange={handleChange}
          >
            <Form.Group className='mb-4 p-3 border rounded bg-light'>
              <Form.Label className='fw-bold me-4'>Data Input Mode:</Form.Label>
              <Form.Check
                type="radio"
                label="Use Raw Values (Auto-Conversion)"
                name="value_type"
                value="RAW"
                checked={values.value_type === 'RAW'}
                onChange={() => setFieldValue('value_type', 'RAW')}
                inline
              />
              <Form.Check
                type="radio"
                label="Use Final/Converted Values"
                name="value_type"
                value="FINAL"
                checked={values.value_type === 'FINAL'}
                onChange={() => setFieldValue('value_type', 'FINAL')}
                inline
              />
              {values.value_type === 'FINAL' && (
                <div className="text-warning mt-2 fw-bold">
                  &#9888; Warning: Sending final values instead of raw values can cause issues with device pings in the future.
                </div>
              )}
            </Form.Group>
            <Row className='my-4'>
              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    type='text'
                    name='sample_type'
                    value={isWaterReport ? 'WATER' : 'SOIL'}
                    placeholder='Sample Type'
                    className={generateClassName(errors, touched, 'sample_type')}
                    onChange={handleChange}
                    disabled={true}
                  />
                  {renderLabel('sample_type', 'Sample Type')}
                </div>
              </Form.Group>

              {/* Sample ID */}
              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    type='text'
                    name='sample_id'
                    value={values.sample_id}
                    placeholder='Enter Sample ID'
                    className={generateClassName(errors, touched, 'sample_id')}
                    onChange={handleChange}
                    disabled={true}
                  />
                  {renderLabel('sample_id', 'Sample ID')}
                </div>
                {errors.sample_id && touched.sample_id ? (
                  <div className='form_errors'> &#9888; {errors.sample_id}</div>
                ) : null}
              </Form.Group>

              {/* Farm name */}
              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    type='text'
                    name='farm_name'
                    value={values.farm_name}
                    placeholder='Enter Farm name'
                    className={generateClassName(errors, touched, 'farm_name')}
                    onChange={handleChange}
                    disabled={true}
                  />
                  {renderLabel('farm_name', 'Farm Name')}
                </div>
                {errors.farm_name && touched.farm_name ? (
                  <div className='form_errors'> &#9888; {errors.farm_name}</div>
                ) : null}
              </Form.Group>

              {!isWaterReport && (
                <>
                {/* Farm size */}
                <Form.Group as={Col} md='6' className='group'>
                  <div className='form-floating'>
                    <Field
                      name='farm_size'
                      value={values.farm_size}
                      component={NumberField}
                      placeholder='Enter Farm size'
                      className={generateClassName(errors, touched, 'farm_size')}
                      onChange={handleChange}
                      disabled={true}
                    />
                    {renderLabel('farm_size', 'Farm Size')}
                  </div>
                  {errors.farm_size && touched.farm_size ? (
                    <div className='form_errors'> &#9888; {errors.farm_size}</div>
                  ) : null}
                </Form.Group>

                {/* Next crop */}
                <Form.Group as={Col} md='6' className='group'>
                  <div className='form-floating'>
                    <Field
                      type='text'
                      name='next_crop'
                      value={values.next_crop}
                      placeholder='Enter Farm name'
                      className={generateClassName(errors, touched, 'next_crop')}
                      onChange={handleChange}
                      disabled={true}
                    />
                    {renderLabel('next_crop', 'Next Crop')}
                  </div>
                  {errors.next_crop && touched.next_crop ? (
                    <div className='form_errors'> &#9888; {errors.next_crop}</div>
                  ) : null}
                </Form.Group>
                </>
              )}

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='ph'
                    value={values.ph}
                    placeholder='Enter PH'
                    className={generateClassName(errors, touched, 'ph')}
                    onChange={handleChange}
                  />
                  {renderLabel('ph', 'PH')}
                </div>
                {errors.ph && touched.ph ? (
                  <div className='form_errors'> &#9888; {errors.ph}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='ec'
                    value={values.ec}
                    placeholder='Enter EC'
                    className={generateClassName(errors, touched, 'ec')}
                    onChange={handleChange}
                  />
                  {renderLabel('ec', 'EC')}
                </div>
                {errors.ec && touched.ec ? (
                  <div className='form_errors'> &#9888; {errors.ec}</div>
                ) : null}
              </Form.Group>

              {!isWaterReport && (
                <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='oc'
                    value={values.oc}
                    placeholder='Enter OC'
                    className={generateClassName(errors, touched, 'oc')}
                    onChange={handleChange}
                  />
                  {renderLabel('oc', 'OC')}
                </div>
                {errors.oc && touched.oc ? (
                  <div className='form_errors'> &#9888; {errors.oc}</div>
                ) : null}
              </Form.Group>
              )}

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='nitrate'
                    value={values.nitrate}
                    placeholder='Enter Nitrate'
                    className={generateClassName(errors, touched, 'nitrate')}
                    onChange={handleChange}
                  />
                  {renderLabel('nitrate', 'Nitrate')}
                </div>
                {errors.nitrate && touched.nitrate ? (
                  <div className='form_errors'> &#9888; {errors.nitrate}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='ammonia'
                    value={values.ammonia}
                    placeholder='Enter Ammonia'
                    className={generateClassName(errors, touched, 'ammonia')}
                    onChange={handleChange}
                  />
                  {renderLabel('ammonia', 'Ammonia')}
                </div>
                {errors.ammonia && touched.ammonia ? (
                  <div className='form_errors'> &#9888; {errors.ammonia}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='phosphorus'
                    value={values.phosphorus}
                    placeholder='Enter Phosphorus'
                    className={generateClassName(errors, touched, 'phosphorus')}
                    onChange={handleChange}
                  />
                  {renderLabel('phosphorus', 'Phosphorus')}
                </div>
                {errors.phosphorus && touched.phosphorus ? (
                  <div className='form_errors'>
                    {' '}
                    &#9888; {errors.phosphorus}
                  </div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='potassium'
                    value={values.potassium}
                    placeholder='Enter Potassium'
                    className={generateClassName(errors, touched, 'potassium')}
                    onChange={handleChange}
                  />
                  {renderLabel('potassium', 'Potassium')}
                </div>
                {errors.potassium && touched.potassium ? (
                  <div className='form_errors'> &#9888; {errors.potassium}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='calcium'
                    value={values.calcium}
                    placeholder='Enter Calcium'
                    className={generateClassName(errors, touched, 'calcium')}
                    onChange={handleChange}
                  />
                  {renderLabel('calcium', 'Calcium')}
                </div>
                {errors.calcium && touched.calcium ? (
                  <div className='form_errors'> &#9888; {errors.calcium}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='magnesium'
                    value={values.magnesium}
                    placeholder='Enter Magnesium'
                    className={generateClassName(errors, touched, 'magnesium')}
                    onChange={handleChange}
                  />
                  {renderLabel('magnesium', 'Magnesium')}
                </div>
                {errors.magnesium && touched.magnesium ? (
                  <div className='form_errors'> &#9888; {errors.magnesium}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='sulphur'
                    value={values.sulphur}
                    placeholder='Enter Sulphur'
                    className={generateClassName(errors, touched, 'sulphur')}
                    onChange={handleChange}
                  />
                  {renderLabel('sulphur', 'Sulphur')}
                </div>
                {errors.sulphur && touched.sulphur ? (
                  <div className='form_errors'> &#9888; {errors.sulphur}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='boron'
                    value={values.boron}
                    placeholder='Enter Boron'
                    className={generateClassName(errors, touched, 'boron')}
                    onChange={handleChange}
                  />
                  {renderLabel('boron', 'Boron')}
                </div>
                {errors.boron && touched.boron ? (
                  <div className='form_errors'> &#9888; {errors.boron}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='iron'
                    value={values.iron}
                    placeholder='Enter Iron'
                    className={generateClassName(errors, touched, 'iron')}
                    onChange={handleChange}
                  />
                  {renderLabel('iron', 'Iron')}
                </div>
                {errors.iron && touched.iron ? (
                  <div className='form_errors'> &#9888; {errors.iron}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='zinc'
                    value={values.zinc}
                    placeholder='Enter Zinc'
                    className={generateClassName(errors, touched, 'zinc')}
                    onChange={handleChange}
                  />
                  {renderLabel('zinc', 'Zinc')}
                </div>
                {errors.zinc && touched.zinc ? (
                  <div className='form_errors'> &#9888; {errors.zinc}</div>
                ) : null}
              </Form.Group>

              <Form.Group as={Col} md='6' className='group'>
                <div className='form-floating'>
                  <Field
                    component={NumberField}
                    name='copper'
                    value={values.copper}
                    placeholder='Enter copper'
                    className={generateClassName(errors, touched, 'copper')}
                    onChange={handleChange}
                  />
                  {renderLabel('copper', 'Copper')}
                </div>
                {errors.copper && touched.copper ? (
                  <div className='form_errors'> &#9888; {errors.copper}</div>
                ) : null}
              </Form.Group>

              {isWaterReport && (
                <>
                <Form.Group as={Col} md='6' className='group'>
                  <div className='form-floating'>
                    <Field
                      name='carbonate'
                      value={values.carbonate}
                      component={NumberField}
                      placeholder='Enter carbonate'
                      className={generateClassName(errors, touched, 'carbonate')}
                      onChange={handleChange}
                    />
                    {renderLabel('carbonate', 'Carbonate')}
                  </div>
                  {errors.carbonate && touched.carbonate ? (
                    <div className='form_errors'> &#9888; {errors.carbonate}</div>
                  ) : null}
                </Form.Group>

                <Form.Group as={Col} md='6' className='group'>
                  <div className='form-floating'>
                    <Field
                      name='bicarbonate'
                      value={values.bicarbonate}
                      component={NumberField}
                      placeholder='bicarbonate'
                      className={generateClassName(errors, touched, 'bicarbonate')}
                      onChange={handleChange}
                    />
                    {renderLabel('bicarbonate', 'Bicarbonate')}
                  </div>
                  {errors.bicarbonate && touched.bicarbonate ? (
                    <div className='form_errors'> &#9888; {errors.bicarbonate}</div>
                  ) : null}
                </Form.Group>

                 <Form.Group as={Col} md='6' className='group'>
                  <div className='form-floating'>
                    <Field
                      name='tds'
                      value={values.tds}
                      component={NumberField}
                      placeholder='tds'
                      className={generateClassName(errors, touched, 'tds')}
                      onChange={handleChange}
                    />
                    {renderLabel('tds', 'TDS')}
                  </div>
                  {errors.tds && touched.tds ? (
                    <div className='form_errors'> &#9888; {errors.tds}</div>
                  ) : null}
                </Form.Group>
                </>
              )}
            </Row>

            <div className='my-3 col col-md-6 mx-auto'>
              <button
                type='submit'
                className='w-100 text-center btn btn-dark'
                disabled={updateStatus === 'loading'}
              >
                {updateStatus === 'loading' && (
                  <Spinner
                    as='span'
                    animation='border'
                    size='sm'
                    role='status'
                    aria-hidden='true'
                    className='me-2'
                  />
                )}
                <span>Update</span>
              </button>
            </div>
          </Form>
        </div>
      )}
    </Formik>
  );
}

export default UpdateForm;
